import { useState, useEffect } from 'react';
// Import auth functions and types from the centralized firebaseService.
// This ensures that all auth-related code uses the same initialized instance and
// can help resolve module resolution errors if there are environment inconsistencies.
import { 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  signOut,
  createUserWithEmailAndPassword,
  getFirebaseServices,
  type User
} from '../services/firebaseService';


export const useAuth = () => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const services = getFirebaseServices();
    if (!services) {
      setLoading(false);
      // App.tsx now handles showing the config view, so we can just stop here.
      return;
    }
    const unsubscribe = onAuthStateChanged(services.auth, (user) => {
      setUser(user);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = async (email: string, password: string): Promise<boolean> => {
    const services = getFirebaseServices();
    if (!services) {
        setError("Firebase 접속에 실패했습니다. firebaseConfig.ts 파일이 올바르게 설정되었는지 확인해주세요.");
        return false;
    }
    setError(null);
    try {
      await signInWithEmailAndPassword(services.auth, email, password);
      return true;
    } catch (e: any) {
      if (e.code === 'auth/invalid-credential' || e.code === 'auth/wrong-password' || e.code === 'auth/user-not-found' || e.code === 'auth/invalid-login-credentials') {
        setError('이메일 또는 비밀번호가 올바르지 않습니다.');
      } else if (e.code === 'auth/invalid-email') {
        setError('유효하지 않은 이메일 형식입니다.');
      } else {
        setError('로그인 중 오류가 발생했습니다. 다시 시도해주세요.');
        console.error(e);
      }
      return false;
    }
  };

  const signup = async (email: string, password: string): Promise<boolean> => {
    const services = getFirebaseServices();
    if (!services) {
      setError("Firebase 접속에 실패했습니다. firebaseConfig.ts 파일이 올바르게 설정되었는지 확인해주세요.");
      return false;
    }
    setError(null);
    try {
      await createUserWithEmailAndPassword(services.auth, email, password);
      return true;
    } catch (e: any) {
      if (e.code === 'auth/email-already-in-use') {
        setError('이미 사용 중인 이메일입니다.');
      } else if (e.code === 'auth/weak-password') {
        setError('비밀번호는 6자리 이상이어야 합니다.');
      } else if (e.code === 'auth/invalid-email') {
        setError('유효하지 않은 이메일 형식입니다.');
      } else {
        setError('회원가입 중 오류가 발생했습니다. 다시 시도해주세요.');
        console.error(e);
      }
      return false;
    }
  };

  const logout = async () => {
    const services = getFirebaseServices();
    if (!services) return;
    try {
      await signOut(services.auth);
    } catch (e) {
      console.error("Error signing out: ", e);
    }
  };

  return { user, loading, error, login, signup, logout };
};